import React, { useEffect, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';

interface Sport {
  id: number;
  name: string;
}

interface Grade {
  id: number;
  sport_id: number;
  grade_name: string;
  age: number;
  as_at_day: number;
  as_at_month: number;
}

interface GradeManagementProps {
  apiBaseUrl: string;
  accessToken: string | null;
  userRoles?: string[];
  userSports?: string[];
}

function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

const GradeManagement: React.FC<GradeManagementProps> = ({ apiBaseUrl, accessToken, userRoles, userSports }) => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Partial<Grade>>({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const columnHelper = createColumnHelper<Grade>();
  const columns = [
    columnHelper.accessor('grade_name', { header: 'Grade Name', cell: info => info.getValue(), enableSorting: true }),
    columnHelper.accessor('age', { header: 'Age (years)', cell: info => info.getValue(), enableSorting: true }),
    columnHelper.accessor(row => `${row.as_at_day.toString().padStart(2, '0')}/${row.as_at_month.toString().padStart(2, '0')}`, {
      id: 'as_at',
      header: 'As At (DD/MM)',
      cell: info => info.getValue(),
      enableSorting: true,
    }),
    columnHelper.accessor('sport_id', { header: 'Sport', cell: info => {
      const sport = sports.find(s => s.id === info.getValue());
      return sport ? sport.name : '';
    }, enableSorting: false }),
  ];

  const table = useReactTable({
    data: grades,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Filter sports for dropdown: admins/trustees see all, sports admins see only their sports
  const sportsForUser = hasRole(userRoles, 'admin') || hasRole(userRoles, 'trustee')
    ? sports
    : sports.filter(s => (userSports || getUserSports(userRoles)).includes(s.name.toLowerCase()));

  useEffect(() => {
    fetch(`${apiBaseUrl}/sports`, {
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
    })
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setSports(data); });
  }, [apiBaseUrl, accessToken]);

  useEffect(() => {
    if (!selectedSportId) { setGrades([]); return; }
    setLoading(true);
    setError(null);
    fetch(`${apiBaseUrl}/grades?sport_id=${selectedSportId}`, {
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
    })
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setGrades(data); else setError('Failed to fetch grades.'); })
      .catch(() => setError('Failed to fetch grades.'))
      .finally(() => setLoading(false));
  }, [selectedSportId, apiBaseUrl, accessToken, saving]);

  const handleEdit = (grade: Grade) => {
    setForm({ ...grade });
    setShowForm(true);
    setFormError('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this grade?')) return;
    setSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/grades/${id}`, {
        method: 'DELETE',
        headers: { ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
      });
      if (response.ok) {
        setGrades(grades => grades.filter(g => g.id !== id));
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to delete grade.');
      }
    } catch {
      alert('Error deleting grade.');
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      if (!form.grade_name || !form.age || !form.as_at_day || !form.as_at_month || !selectedSportId) {
        setFormError('All fields are required.');
        setSaving(false);
        return;
      }
      const method = form.id ? 'PUT' : 'POST';
      const url = form.id ? `${apiBaseUrl}/grades/${form.id}` : `${apiBaseUrl}/grades`;
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
        body: JSON.stringify({ ...form, sport_id: selectedSportId }),
      });
      const data = await response.json();
      if (response.ok) {
        setShowForm(false);
        setForm({});
        setSaving(false);
        setGrades([]); // Will refetch
      } else {
        setFormError(data.message || 'Failed to save grade.');
      }
    } catch {
      setFormError('Error saving grade.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Grade Management</h2>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <select
          className="w-full sm:w-72 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={selectedSportId ?? ''}
          onChange={e => setSelectedSportId(Number(e.target.value) || null)}
        >
          <option value="">Select a sport...</option>
          {sportsForUser.map(sport => (
            <option key={sport.id} value={sport.id}>{sport.name}</option>
          ))}
        </select>
        <button
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
          onClick={() => { setShowForm(true); setForm({ sport_id: selectedSportId || undefined }); setFormError(''); }}
          disabled={!selectedSportId}
        >
          Add Grade
        </button>
      </div>
      {loading ? (
        <div>Loading grades...</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow-md">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-200">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b cursor-pointer select-none"
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() ? (
                        <span className="ml-1">{header.column.getIsSorted() === 'asc' ? '▲' : '▼'}</span>
                      ) : null}
                    </th>
                  ))}
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">Actions</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="py-3 px-4 border-b border-gray-200 text-sm text-gray-800">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  <td className="py-3 px-4 border-b border-gray-200 text-sm">
                    <button
                      onClick={() => handleEdit(row.original)}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-md mr-2 shadow-sm transition duration-300 ease-in-out"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(row.original.id)}
                      className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-md shadow-sm transition duration-300 ease-in-out"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <form onSubmit={handleFormSubmit} className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md relative">
            <h3 className="text-xl font-bold mb-4">{form.id ? 'Edit Grade' : 'Add Grade'}</h3>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Grade Name</label>
              <input
                type="text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={form.grade_name || ''}
                onChange={e => setForm(f => ({ ...f, grade_name: e.target.value }))}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Age (years)</label>
              <input
                type="number"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={form.age || ''}
                onChange={e => setForm(f => ({ ...f, age: Number(e.target.value) }))}
                required
                min={1}
              />
            </div>
            <div className="mb-4 flex gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">As At Day</label>
                <select
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={form.as_at_day || ''}
                  onChange={e => setForm(f => ({ ...f, as_at_day: Number(e.target.value) }))}
                  required
                >
                  <option value="">Day</option>
                  {[...Array(31)].map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">As At Month</label>
                <select
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={form.as_at_month || ''}
                  onChange={e => setForm(f => ({ ...f, as_at_month: Number(e.target.value) }))}
                  required
                >
                  <option value="">Month</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
              </div>
            </div>
            {formError && <div className="mb-4 text-red-600">{formError}</div>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-md"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >Cancel</button>
              <button
                type="submit"
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
                disabled={saving}
              >{saving ? 'Saving...' : (form.id ? 'Update' : 'Add')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default GradeManagement;
